import {CloseIcon} from "@chakra-ui/icons";
import {Box, Flex, IconButton, Progress, Text, VStack} from "@chakra-ui/react";
import {useUploadStore, type UploadItem} from "../store/useUploadStore.ts";

interface UploadRowProps {
  readonly upload: UploadItem;
  readonly onDismiss: (uploadId: string) => void;
}

interface UploadPresentation {
  readonly progressColor: string;
  readonly statusMessage: string;
  readonly statusRole: "alert" | "status";
}

const MAX_DISPLAY_FILE_NAME_LENGTH = 46;
const FILE_NAME_PREFIX_LENGTH = 19;

const compactFileName = (fileName: string): string => {
  if (fileName.length <= MAX_DISPLAY_FILE_NAME_LENGTH) return fileName;
  const extensionIndex = fileName.lastIndexOf(".");
  const hasExtension = extensionIndex > 0;
  const extension = hasExtension ? fileName.slice(extensionIndex) : "";
  const stem = hasExtension ? fileName.slice(0, extensionIndex) : fileName;
  const suffixLength = MAX_DISPLAY_FILE_NAME_LENGTH - FILE_NAME_PREFIX_LENGTH - extension.length - 1;
  return `${stem.slice(0, FILE_NAME_PREFIX_LENGTH)}…${stem.slice(-suffixLength)}${extension}`;
};

const assertNever = (value: never): never => {
  throw new Error(`Unexpected upload state: ${JSON.stringify(value)}`);
};

const getUploadPresentation = (upload: UploadItem): UploadPresentation => {
  switch (upload.status) {
    case "uploading":
      return {progressColor: "#8b59f1", statusMessage: "Uploading", statusRole: "status"};
    case "success":
      return {progressColor: "var(--chakra-colors-green-400)", statusMessage: "Ready", statusRole: "status"};
    case "error":
      return {progressColor: "var(--chakra-colors-red-400)", statusMessage: upload.message, statusRole: "alert"};
    default:
      return assertNever(upload);
  }
};

const UploadRow = ({upload, onDismiss}: UploadRowProps) => {
  const presentation = getUploadPresentation(upload);
  const displayedFileName = compactFileName(upload.fileName);

  return (
    <Flex
      role="listitem"
      direction="column"
      gap={1.5}
      minH="4.5rem"
      width="full"
      px={3}
      py={2}
      bg="n.500"
      color="n.100"
      borderRadius="xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      boxShadow="0 8px 24px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.04)"
    >
      <Flex align="flex-start" gap={2} width="full" minW={0}>
        <Box data-testid="upload-details" minW={0} flex="1" pt={0.5}>
          <Text noOfLines={1} title={upload.fileName} fontSize="sm" fontWeight="medium" lineHeight="1.2">
            {displayedFileName}
          </Text>
          <Text
            role={presentation.statusRole}
            noOfLines={1}
            title={presentation.statusMessage}
            mt={1}
            fontSize="xs"
            lineHeight="1.1"
            color={upload.status === "error" ? "red.200" : "n.300"}
          >
            {presentation.statusMessage}
          </Text>
        </Box>
        <Text
          minW="2.5rem"
          flexShrink={0}
          pt={0.5}
          textAlign="right"
          fontSize="sm"
          fontWeight="semibold"
          color="n.200"
          sx={{fontVariantNumeric: "tabular-nums"}}
        >
          {upload.progress}%
        </Text>
        <IconButton
          aria-label={`Dismiss upload ${upload.fileName}`}
          icon={<CloseIcon boxSize={2.5} />}
          minW={10}
          minH={10}
          mt={-1}
          mr={-1}
          size="sm"
          variant="ghost"
          color="n.300"
          borderRadius="full"
          _hover={{bg: "n.600", color: "n.100"}}
          _active={{transform: "scale(0.96)"}}
          transitionProperty="background-color, color, transform"
          transitionDuration="0.15s"
          onClick={() => onDismiss(upload.id)}
        />
      </Flex>
      <Box data-testid="upload-meter" width="full" pr={1}>
        <Progress
          aria-label={`Upload progress for ${upload.fileName}`}
          data-progress-color={presentation.progressColor}
          value={upload.progress}
          width="full"
          height="0.625rem"
          bg="n.700"
          borderRadius="full"
          overflow="hidden"
          sx={{"& > div:first-of-type": {backgroundColor: presentation.progressColor, transitionProperty: "width", transitionDuration: "0.2s", transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)"}}}
        />
      </Box>
    </Flex>
  );
};

const UploadToaster = () => {
  const uploads = useUploadStore((state) => state.uploads);
  const dismissUpload = useUploadStore((state) => state.dismissUpload);

  if (uploads.length === 0) return null;

  return (
    <VStack
      role="list"
      aria-label="Current uploads"
      aria-live="polite"
      align="stretch"
      spacing={2}
      width="clamp(28rem, 36vw, 38rem)"
      maxH="11rem"
      overflowY="auto"
    >
      {uploads.map((upload) => <UploadRow key={upload.id} upload={upload} onDismiss={dismissUpload} />)}
    </VStack>
  );
};

export default UploadToaster;
